import React, { useEffect, useRef } from 'react';
import { uniq } from 'lodash';
import { DateTime } from 'luxon';
import { useApolloClient } from '@apollo/client';
import * as actions from '~/actions';
import { ORDER_ORIGIN, SERVICE_TYPE } from '~../__queries__/globalTypes';
import { useStorage } from '~Components/Storage';
import { useTableUniverse } from '~Components/TableUniverse';
import { useTimeManager } from '~Components/TimeManager';
import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import { VARIATION_NAME_QUERY } from '~Scenes/EventedKitchenDisplay/Components/VariationName';
import {
  VariationNameQuery,
  VariationNameQueryVariables,
} from '~Scenes/EventedKitchenDisplay/Components/__queries__/VariationNameQuery';
import { itemInBong } from '~Scenes/POS/Queries/__queries__';
import { ITEM_FRAGMENT } from '~Scenes/POS/Queries/query';
import { QrPayLaterVariationName } from './__queries__';
import { VARIATION_NAME_FRAGMENT } from './queries';
import {
  BUFFER_MS,
  MenuLookup,
  PRINTED_STORAGE_KEY,
  PrintedEventIds,
  QrPayLaterItemEvent,
  VARIATION_FETCH_TIMEOUT_MS,
  WATCHING_SINCE_STORAGE_KEY,
  groupByOrder,
  isLate,
  isQrPayLaterItemEvent,
  parsePrintedEventIds,
  parseWatchingSince,
  partitionNewEvents,
  serializePrintedEventIds,
  toLocalItem,
} from './utils';

const loadPrintedEventIds = (nowMs: number): PrintedEventIds => {
  try {
    return parsePrintedEventIds(window.localStorage.getItem(PRINTED_STORAGE_KEY), nowMs);
  } catch {
    return new Map();
  }
};

/** Returns false if the ids could not be persisted (e.g. storage full or blocked) */
const savePrintedEventIds = (eventIds: string[], nowMs: number): boolean => {
  const printed = loadPrintedEventIds(nowMs);
  eventIds.forEach((eventId) => printed.set(eventId, nowMs));

  try {
    window.localStorage.setItem(PRINTED_STORAGE_KEY, serializePrintedEventIds(printed));
    return true;
  } catch {
    return false;
  }
};

/**
 * Returns the persisted watching-since time, or starts watching now if there is none.
 * `persisted` is false if a new start time could not be saved (a reload would then reset it).
 */
const loadOrStartWatchingSince = (nowMs: number) => {
  try {
    const stored = parseWatchingSince(window.localStorage.getItem(WATCHING_SINCE_STORAGE_KEY));
    if (stored !== null) {
      return { watchingSinceMs: stored, resumed: true, persisted: true };
    }
  } catch {
    // Fall through and start watching now
  }

  try {
    window.localStorage.setItem(WATCHING_SINCE_STORAGE_KEY, String(nowMs));
    return { watchingSinceMs: nowMs, resumed: false, persisted: true };
  } catch {
    return { watchingSinceMs: nowMs, resumed: false, persisted: false };
  }
};

const clearWatchingSince = () => {
  try {
    window.localStorage.removeItem(WATCHING_SINCE_STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is blocked
  }
};

interface PendingOrder {
  events: QrPayLaterItemEvent[];
  timer: ReturnType<typeof setTimeout>;
}

/**
 * QR "order now & pay later" orders are written by galaxy as plain table events, not as an
 * Order, so they never reach the online order bong flow. This watches for those events and
 * queues a physical-only bong (galaxy already created the KDS order) on the enabled device.
 */
const QrPayLaterBongs: React.FC = () => {
  const [storage, , storageLoading] = useStorage();
  const tableEvents = useSelector((state) => state.tableEvents);
  const tables = useSelector((state) => state.tables);
  const universe = useTableUniverse();
  const timeManager = useTimeManager();
  const client = useApolloClient();
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const logger = useLogger('qr-pay-later-bongs');

  const enabled = storage.enableBongPrints && storage.printQrPayLaterBongs;

  // Orders created before this are history. Null while not watching (disabled or storage loading)
  const watchingSinceMs = useRef<number | null>(null);
  const seenEventIds = useRef(new Set<string>());
  const pendingOrders = useRef(new Map<string, PendingOrder>());

  const getCourseType = (itemId: string) => {
    try {
      const item = client.readFragment<itemInBong>({
        id: `Item${itemId}`,
        fragment: ITEM_FRAGMENT,
      });
      return item?.courseType ?? undefined;
    } catch {
      return undefined;
    }
  };

  const readCachedVariationName = (variationId: string): string | null => {
    try {
      const variation = client.readFragment<QrPayLaterVariationName>({
        id: variationId,
        fragment: VARIATION_NAME_FRAGMENT,
      });
      return variation ? i18n(variation.nameLang) || variation.name : null;
    } catch {
      return null;
    }
  };

  const queryVariationName = async (variationId: string): Promise<string | null> => {
    try {
      const result = await client.query<VariationNameQuery, VariationNameQueryVariables>({
        query: VARIATION_NAME_QUERY,
        variables: { id: variationId },
        fetchPolicy: 'network-only',
      });
      const variation = result.data?.variation;
      return variation ? i18n(variation.nameLang) || variation.name : null;
    } catch (e) {
      logger.warn('Failed to fetch removed variation name', { variationId, errorData: e });
      return null;
    }
  };

  // A slow network must not hold back the kitchen ticket, so give up after a short wait
  const fetchVariationName = async (variationId: string): Promise<string | null> => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<null>((resolve) => {
      timeoutId = setTimeout(() => {
        logger.warn('Timed out fetching removed variation name', { variationId });
        resolve(null);
      }, VARIATION_FETCH_TIMEOUT_MS);
    });

    try {
      return await Promise.race([queryVariationName(variationId), timeout]);
    } finally {
      clearTimeout(timeoutId);
    }
  };

  /**
   * Galaxy sends removed variations with the id as name. Read the real name from the cached
   * menu, and fetch it when the variation was added to the menu after this device loaded it.
   */
  const resolveRemovedVariationNames = async (events: QrPayLaterItemEvent[]) => {
    const variationIds = uniq(
      events.flatMap((event) => (event.removedVariations ?? []).map((v) => v.id)),
    );
    const names = new Map<string, string>();

    await Promise.all(
      variationIds.map(async (variationId) => {
        const name =
          readCachedVariationName(variationId) ?? (await fetchVariationName(variationId));

        if (name) {
          names.set(variationId, name);
        } else {
          logger.warn('Removed variation name not found, printing its id', { variationId });
        }
      }),
    );

    return names;
  };

  const flushOrder = async (key: string) => {
    const pending = pendingOrders.current.get(key);
    if (!pending) {
      return;
    }
    pendingOrders.current.delete(key);

    const [order] = groupByOrder(pending.events);
    const removedVariationNames = await resolveRemovedVariationNames(order.events);
    const menuLookup: MenuLookup = {
      getCourseType,
      getVariationName: (variationId) => removedVariationNames.get(variationId) ?? null,
    };

    const tableId =
      order.tableId ?? universe.cheques.find((c) => c.id === order.chequeId)?.tableId ?? null;
    const table = tables.find((t) => t.id === tableId);
    const eventIds = order.events.map((e) => e.eventId);

    // Orders that reach us late (network drop, reload) still print, but say when they were placed.
    // createdAt is an ISO string; formatted in device-local time like the ticket's date line
    const delayed = isLate(order.createdAt, timeManager.now().getTime());
    const orderedAt = DateTime.fromISO(order.createdAt).toFormat('HH:mm');

    if (!table) {
      logger.warn('Table not found for QR pay later order, printing without table name', {
        chequeId: order.chequeId,
        tableId,
      });
    }

    dispatch({
      type: actions.PRINT_PHYSICAL_BONG,
      items: order.events.map((event) => toLocalItem(event, menuLookup)),
      order: {
        id: order.chequeId,
        orderNumber: table?.code ?? order.chequeId,
        // Gives the "Table <code>" title in BongMaster
        chequeName: table?.code,
        // Gives the right floorplan on the bong
        tableId: tableId ?? undefined,
        createdAt: order.createdAt,
        // Printed in an outlined box by the default ticket templates
        comment: delayed ? i18n('QrPayLaterBongs.Delayed', orderedAt) : '',
      },
      serviceType: SERVICE_TYPE.EAT_HERE,
      origin: ORDER_ORIGIN.APP,
    });

    const persisted = savePrintedEventIds(eventIds, timeManager.now().getTime());

    const logContext = {
      chequeId: order.chequeId,
      tableId,
      rowCount: eventIds.length,
      eventIds,
      orderCreatedAt: order.createdAt,
    };

    if (delayed) {
      logger.warn('Queued delayed QR pay later bong', logContext);
    } else {
      logger.info('Queued QR pay later bong', logContext);
    }

    if (!persisted) {
      logger.warn('Could not persist printed QR pay later events, a reload may reprint them', {
        eventIds,
      });
    }
  };

  // Timers outlive the render that created them, so always call the latest flushOrder
  const flushOrderRef = useRef(flushOrder);
  flushOrderRef.current = flushOrder;

  const bufferOrderEvents = (key: string, events: QrPayLaterItemEvent[]) => {
    const pending = pendingOrders.current.get(key);
    if (pending) {
      clearTimeout(pending.timer);
    }

    pendingOrders.current.set(key, {
      events: [...(pending?.events ?? []), ...events],
      timer: setTimeout(() => void flushOrderRef.current(key), BUFFER_MS),
    });
  };

  const clearPendingOrders = () => {
    pendingOrders.current.forEach((pending) => clearTimeout(pending.timer));
    pendingOrders.current.clear();
  };

  /**
   * Start (or resume after a reload) watching when enabled, and forget the start time when
   * disabled so turning it back on doesn't print orders from while it was off.
   * Skipped while storage loads: the defaults would read as "disabled" and wipe the start time.
   */
  useEffect(() => {
    if (storageLoading) {
      return;
    }

    if (!enabled) {
      clearPendingOrders();
      if (watchingSinceMs.current !== null) {
        logger.info('Stopped watching for QR pay later orders');
      }
      watchingSinceMs.current = null;
      clearWatchingSince();
      return;
    }

    const watching = loadOrStartWatchingSince(timeManager.now().getTime());
    watchingSinceMs.current = watching.watchingSinceMs;

    logger.info('Watching for QR pay later orders', {
      watchingSince: new Date(watching.watchingSinceMs).toISOString(),
      resumed: watching.resumed,
    });

    if (!watching.persisted) {
      logger.warn('Could not persist QR pay later watching start, a reload will reset it');
    }
  }, [enabled, storageLoading]);

  useEffect(() => clearPendingOrders, []);

  useEffect(() => {
    const watchingSince = watchingSinceMs.current;
    if (!enabled || watchingSince === null) {
      return;
    }

    const unseenEvents = tableEvents.filter((e) => !seenEventIds.current.has(e.eventId));
    unseenEvents.forEach((e) => seenEventIds.current.add(e.eventId));

    const qrEvents = unseenEvents.filter(isQrPayLaterItemEvent);
    if (qrEvents.length === 0) {
      return;
    }

    const nowMs = timeManager.now().getTime();
    const { toPrint, alreadyPrinted, beforeWatching, tooOld } = partitionNewEvents(
      qrEvents,
      nowMs,
      loadPrintedEventIds(nowMs),
      watchingSince,
    );

    if (beforeWatching.length > 0) {
      logger.info('Skipped QR pay later events from before watching started', {
        eventIds: beforeWatching.map((e) => e.eventId),
      });
    }

    // Should only happen after a very long outage, so make it loud
    if (tooOld.length > 0) {
      logger.warn('Skipped QR pay later events older than the catch-up window, not printed', {
        eventIds: tooOld.map((e) => e.eventId),
        createdAts: uniq(tooOld.map((e) => e.createdAt)),
      });
    }

    if (alreadyPrinted.length > 0) {
      logger.info('Skipped already printed QR pay later events', {
        eventIds: alreadyPrinted.map((e) => e.eventId),
      });
    }

    groupByOrder(toPrint).forEach((order) => bufferOrderEvents(order.key, order.events));
  }, [enabled, storageLoading, tableEvents]);

  return null;
};

export default QrPayLaterBongs;
